import React, { useEffect, useRef } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { Modal } from '@uniquegood/realworld-studio-design';
import { useDynamicPostRequest, useModalState, usePlanLimits, useRequest } from '@/hooks';
import { CORE_API_BASE_URL, logout } from '@/api';
import ExclamationModalContent from '@/components/ExclamationModalContent';
import SimultaneousNoticeModal from '@/components/SimultaneousNoticeModal';
import { OAUTH_LOGIN_URI } from '@/auth';
import { useUnsavedChanges } from '@/providers/UnsavedChanges';
import { ClickEventName, PageViewEventName, track } from '@/track';
import { isRealworldAdminRole, isSchoolAdminRole } from '@/utils/roleAccess';

interface SSEBaseMessage {
  message: string;
  timestamp: string;
}
interface ConnectedMessageData extends SSEBaseMessage {
  type: 'Connected';
  data: {
    connectionId: string;
    applicationId: string;
    currentDeviceCount: number;
    maxDeviceCount: number;
    deviceId: string;
    hasOtherTabConnection: boolean;
  };
}

interface HeartbeatMessageData extends SSEBaseMessage {
  type: 'Heartbeat';
  data: null;
}

interface DisconnectRequestMessageData extends SSEBaseMessage {
  type: 'DisconnectRequest';
  data: null;
}

interface SessionLimitExceededMessageData extends SSEBaseMessage {
  type: 'SessionLimitExceeded';
  data: {
    currentCount: number;
    maxCount: number;
  };
}

interface ErrorMessageData extends SSEBaseMessage {
  type: 'Error';
  data: {
    errorCode: string;
  };
}

interface NewDeviceConnectedMessageData extends SSEBaseMessage {
  type: 'NewDeviceConnected';
  data: null;
}

type SseMessage =
  | ConnectedMessageData
  | HeartbeatMessageData
  | DisconnectRequestMessageData
  | SessionLimitExceededMessageData
  | ErrorMessageData
  | NewDeviceConnectedMessageData;

/**
 * 동시 접속 제한 SSE Provider
 *
 * SSE를 통해 서버에서 동시 접속 이벤트를 받아 전역 모달을 표시합니다.
 *
 * @example
 * ```tsx
 * // App.tsx
 * <SimultaneousAccessProvider>
 *   <Router>...</Router>
 * </SimultaneousAccessProvider>
 * ```
 */
export default function SimultaneousAccessProvider({ children }: { children: React.ReactNode }) {
  const { modal, openModal, closeModal } = useModalState();
  const history = useHistory();
  const location = useLocation();
  const { saveAll, hasSaveableChanges } = useUnsavedChanges();

  const abortControllerRef = useRef<AbortController | null>(null); // 연결 취소용

  // stale closure 방지를 위한 ref
  const connectionIdRef = useRef<string | null>(null);
  const appIdRef = useRef<string | undefined>(undefined);
  const accessTokenRef = useRef<string | null>(null);
  const targetAppIdRef = useRef<string | null>(null); // state 대신 ref 사용 (동기적 업데이트)

  // 새로고침 감지를 위한 상수
  const REFRESH_GRACE_PERIOD_MS = 5000; // 새로고침 후 5초간 DisconnectRequest 무시
  const REFRESH_STORAGE_KEY = 'sse_last_unload_time';

  // appId가 있을 때만 플랜 정보 가져오기 (usePlanLimits는 appId가 없으면 에러 발생 가능)
  const match = matchPath<{ appId: string }>(location.pathname, {
    path: '/apps/:appId',
    exact: false
  });
  const appId = match?.params?.appId;

  const { mutate: mutateApplicationInfo } = useRequest<
    CommonResponseModel<ApplicationInfoResponseModel>
  >(appId ? `/apps/${appId}/info` : null);
  const { plan } = usePlanLimits();
  const { data: me, error: meError, mutate: mutateMe } = useRequest<MyInfo>(
    appId ? `/api/me` : null
  );
  const isRoleResolved = Boolean(me || meError);
  const isConcurrentSessionExemptRole =
    isRealworldAdminRole(me?.role) || isSchoolAdminRole(me?.role);

  const { postByPath: postDisconnectOldest } = useDynamicPostRequest();

  // 인증 토큰 확인
  const accessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;

  // ref 동기화
  useEffect(() => {
    appIdRef.current = appId;
  }, [appId]);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  // 페이지 종료 시 서버에 disconnect 요청 (새로고침/탭 닫기 대응)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentAppId = appIdRef.current;
      const currentConnectionId = connectionIdRef.current;
      const currentAccessToken = accessTokenRef.current;

      // 새로고침 감지를 위해 현재 시간 저장
      sessionStorage.setItem(REFRESH_STORAGE_KEY, Date.now().toString());

      if (currentAppId && currentConnectionId && currentAccessToken) {
        const url = `${CORE_API_BASE_URL}/api/applications/${currentAppId}/session/disconnect?connectionId=${currentConnectionId}`;
        // keepalive: true를 사용해 페이지 종료 후에도 요청 완료
        fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${currentAccessToken}`,
            'x-rwd-api-version': '1.0'
          },
          keepalive: true
        }).catch(() => {
          // 무시 - 페이지가 닫히는 중이므로 에러 처리 불필요
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const disconnect = async (message: string) => {
    // 🔥 맨 먼저 현재 값을 지역 변수에 저장하고 즉시 초기화
    // 이렇게 해야 동시 호출 시 중복 요청 방지 + 새 연결에 영향 안 줌
    const currentAppId = appIdRef.current;
    const currentConnectionId = connectionIdRef.current;
    const currentAccessToken = accessTokenRef.current;
    const currentAbortController = abortControllerRef.current;

    // 즉시 초기화하여 중복 호출 및 새 연결 영향 방지
    connectionIdRef.current = null;
    abortControllerRef.current = null;

    console.log('🔌 [SSE] 연결을 종료합니다.', message, { connectionId: currentConnectionId });

    // 1. 클라이언트 측 SSE 스트림 abort (먼저 실행하여 즉시 연결 끊기)
    if (currentAbortController) {
      currentAbortController.abort();
    }

    // 2. 서버에 명시적으로 disconnect 요청 (저장한 지역 변수 사용)
    if (currentAppId && currentConnectionId && currentAccessToken) {
      try {
        const url = `${CORE_API_BASE_URL}/api/applications/${currentAppId}/session/disconnect?connectionId=${currentConnectionId}`;
        await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${currentAccessToken}`,
            'x-rwd-api-version': '1.0'
          }
        });
        console.log('✅ [SSE] 서버에 disconnect 요청 완료');
      } catch (error) {
        console.warn('⚠️ [SSE] disconnect 요청 실패:', error);
      }
    }
  };

  const connectSSE = async (appId: string, token: string, forceConnect: boolean) => {
    await disconnect('connectSSE');

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const url = `${CORE_API_BASE_URL}/api/applications/${appId}/session/connect?forceConnect=${forceConnect}`;

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
          'x-rwd-api-version': '1.0'
        },
        signal: controller.signal,
        credentials: 'include'
      });

      // ✅ 상태 코드별 처리가 명확함
      if (response.status === 409) {
        console.warn('🚨 [SSE] 409 중복 접속');
        handleIncommingSessison();
        return; // 그냥 종료
      }

      if (response.status === 401) {
        await logout();
        window.location.href = OAUTH_LOGIN_URI;
        return;
      }

      // ✅ 200 OK - 스트림 읽기 시작
      if (response.ok && response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const readStream = async (): Promise<void> => {
          const { done, value } = await reader.read();
          if (done || controller.signal.aborted) return;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          // eslint-disable-next-line no-await-in-loop -- 메시지 순서 보장을 위해 순차 처리 필요
          for (const line of lines) {
            if (line.startsWith('data:')) {
              const jsonStr = line.substring(5).trim();
              if (jsonStr) {
                const message: SseMessage = JSON.parse(jsonStr);
                // eslint-disable-next-line no-await-in-loop
                await handleSseMessage(message);
              }
            }
          }

          await readStream();
        };

        await readStream();
      }

      const body = await response.json();

      if (body.errorTitle === 'MaxChannelConnectionExceeded') {
        handleIncommingSessison();
        return;
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      console.error('SSE 에러:', err);
    }
  };

  const handleSseMessage = async (message: SseMessage) => {
    switch (message.type) {
      case 'Connected':
        console.log('🟢 연결됨, connectionId:', message.data);
        connectionIdRef.current = message.data.connectionId;
        break;
      case 'Heartbeat':
        console.log('💓 Heartbeat');
        break;
      case 'DisconnectRequest': {
        console.log('🔴 연결 해제 요청');

        // 새로고침으로 인한 거짓 DisconnectRequest인지 확인
        const lastUnloadTime = sessionStorage.getItem(REFRESH_STORAGE_KEY);
        if (lastUnloadTime) {
          const timeSinceUnload = Date.now() - parseInt(lastUnloadTime, 10);
          if (timeSinceUnload < REFRESH_GRACE_PERIOD_MS) {
            console.log(
              `⏳ [SSE] 새로고침 감지됨 (${timeSinceUnload}ms 전). DisconnectRequest 무시.`
            );
            // sessionStorage 정리
            sessionStorage.removeItem(REFRESH_STORAGE_KEY);
            break;
          }
        }

        handleOldestSession();
        break;
      }
      case 'SessionLimitExceeded':
        console.log('⚠️ 세션 한도 초과:', message.data);
        break;
      case 'Error':
        console.error('❌ SSE 에러:', message.data.errorCode);
        break;
      case 'NewDeviceConnected':
        console.log('🟢 새로운 기기 연결:', message);
        openSimultaneousNoticeModal();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    // 1. URL에서 appId 추출 (/apps/:appId)
    const match = matchPath<{ appId: string }>(location.pathname, {
      path: '/apps/:appId',
      exact: false
    });
    const currentAppId = match?.params?.appId;

    // 2. 앱 ID가 없거나 토큰이 없으면 연결 정리하고 종료
    if (!currentAppId || !accessToken) {
      disconnect('appId or accessToken is null');
      targetAppIdRef.current = null;
      return undefined;
    }

    // 역할 정보가 준비되기 전에는 연결하지 않아서 초기 409 모달 노출을 방지
    if (!isRoleResolved) {
      return undefined;
    }

    // RealWorld Admin / School.Admin 은 동시 접속 제한 대상에서 제외
    if (isConcurrentSessionExemptRole) {
      disconnect('concurrent session exempt role');
      targetAppIdRef.current = null;
      return undefined;
    }

    // 3. 앱 ID가 바뀌었으면 기존 연결 끊고 새로 연결 (기본 force=false)
    // ref를 사용해 동기적으로 비교 및 업데이트
    if (targetAppIdRef.current !== currentAppId) {
      targetAppIdRef.current = currentAppId; // 즉시 동기적으로 업데이트
      connectSSE(currentAppId, accessToken, false);
    }

    // Cleanup: appId가 변경되거나 언마운트될 때만 연결 종료
    // (dependency가 appId이므로 같은 appId 내에서 라우트 변경 시에는 실행되지 않음)
    return () => {
      disconnect('appId changed or unmount');
    };
  }, [appId, accessToken, isRoleResolved, isConcurrentSessionExemptRole]);

  const handleOldestSession = React.useCallback(async () => {
    // 저장 가능한 변경사항이 있으면 먼저 저장 시도
    if (hasSaveableChanges()) {
      console.log('🔥 저장 가능한 변경사항이 있어 먼저 저장 시도');
      try {
        const saveSuccess = await saveAll();
        if (!saveSuccess) {
          console.warn('자동 저장 중 일부 오류가 발생했지만 계속 진행합니다.');
        }
      } catch (error) {
        console.error('자동 저장 중 오류 발생:', error);
        // 저장 실패해도 로그아웃은 진행
      }
    }
    const applicationInfo = await mutateApplicationInfo();
    const meInfo = await mutateMe();

    const channelInfo = applicationInfo?.data;

    await logout();
    history.push('/');

    openModal({
      size: 'small',
      title: '다른 기기에서 접속했어요',
      hasCloseIcon: true,
      canBackdropClose: false,
      onClose: closeModal,
      primaryAction: {
        content: '확인',
        type: 'primary',
        onAction: () => {
          closeModal();
          track.onClick({
            clickEventName: ClickEventName.click_plan_modal_concurrent_session_existing_dismiss,
            params: {
              channel_id: appId ?? '',
              channel_name: channelInfo?.name ?? '',
              use_purpose_type: channelInfo?.usePurposeType ?? '',
              plan_type: plan ?? '',
              user_id: meInfo?.id ?? ''
            }
          });
        }
      },
      children: (
        <ExclamationModalContent
          description={`다른 기기에서 로그인하여 이 기기에서 로그아웃 됐어요.\n최근 편집 내용은 자동 저장됐으니 콘텐츠 내용을 확인해 주세요.`}
        />
      )
    });

    track.onPageViewMixpanel(PageViewEventName.view_plan_modal_concurrent_session_existing, {
      channel_id: appId ?? '',
      channel_name: channelInfo?.name ?? '',
      use_purpose_type: channelInfo?.usePurposeType ?? '',
      plan_type: plan ?? '',
      user_id: meInfo?.id ?? ''
    });
  }, [
    history,
    openModal,
    closeModal,
    saveAll,
    hasSaveableChanges,
    mutateApplicationInfo,
    mutateMe
  ]);

  const handleIncommingSessison = React.useCallback(async () => {
    const match = matchPath<{ appId: string }>(location.pathname, {
      path: '/apps/:appId',
      exact: false
    });
    const currentAppId = match?.params?.appId;

    if (!currentAppId) {
      return;
    }

    const handleLoginButtonClick = async () => {
      postDisconnectOldest(`/api/applications/${currentAppId}/session/disconnect-oldest`).then(
        () => {
          closeModal();
          const token = accessTokenRef.current;
          if (token) {
            connectSSE(currentAppId, token, false);
          }
        }
      );
    };

    const handleCloseIcon = async () => {
      await logout();
      window.location.href = '/auth/login';
    };

    const applicationInfo = await mutateApplicationInfo();
    const meInfo = await mutateMe();

    const channelInfo = applicationInfo?.data;
    openModal({
      size: 'small',
      title: '다른 기기에서 이미 접속했어요',
      hasCloseIcon: true,
      canBackdropClose: false,
      onClose: handleCloseIcon,
      primaryAction: {
        content: '로그인',
        type: 'primary',
        onAction: () => {
          track.onClick({
            clickEventName: ClickEventName.click_plan_modal_concurrent_session_incoming_login,
            params: {
              channel_id: appId ?? '',
              channel_name: channelInfo?.name ?? '',
              use_purpose_type: channelInfo?.usePurposeType ?? '',
              plan_type: plan ?? '',
              user_id: meInfo?.id ?? ''
            }
          });

          handleLoginButtonClick();
        }
      },
      children: (
        <ExclamationModalContent description="다른 기기의 접속을 종료하고 새로 로그인하시겠어요?" />
      )
    });

    track.onPageViewMixpanel(PageViewEventName.view_plan_modal_concurrent_session_incoming, {
      channel_id: appId ?? '',
      channel_name: channelInfo?.name ?? '',
      use_purpose_type: channelInfo?.usePurposeType ?? '',
      plan_type: plan ?? '',
      user_id: meInfo?.id ?? ''
    });
  }, [location.pathname, history, openModal, closeModal, postDisconnectOldest]);

  const openSimultaneousNoticeModal = React.useCallback(() => {
    openModal({
      size: 'medium',
      title: '다른 브라우저에서 접속 중이에요',
      hasCloseIcon: true,
      canBackdropClose: false,
      primaryAction: {
        content: '확인',
        type: 'primary',
        onAction: () => closeModal()
      },
      children: <SimultaneousNoticeModal />
    });
  }, [openModal, closeModal]);

  return (
    <>
      {children}
      <Modal {...modal} />
    </>
  );
}
